<template>
  <v-container>
    <v-btn @click="newService">
      New Service
    </v-btn>
    <v-treeview
      :items="services"
      item-children="tags"
      item-key="title"
      item-text="title"
      open-on-click
      hoverable
    >
      <template v-slot:append="{ item }">
        <v-btn v-if="item.tags" title="Add Tag" icon @click.stop="newTag(item)">
          <v-icon>add</v-icon>
        </v-btn>
        <v-btn
          icon
          :title="item.tags ? 'Edit Service' : 'Edit Tag'"
          @click.stop="item.tags ? editService(item) : editTag(item)"
        >
          <v-icon>edit</v-icon>
        </v-btn>
        <v-btn
          icon
          @click.stop="item.tags ? deleteService(item.id) : deleteTag(item.id)"
        >
          <v-icon>delete</v-icon>
        </v-btn>
      </template>
    </v-treeview>
    <v-dialog v-model="serviceDialogOpen" max-width="800px">
      <v-card flat tile height="100%" class="pa-4">
        <v-card-title class="display-1 secondary--text">
          {{ serviceAlreadyExists ? "Edit Service" : "Add a New Service" }}
        </v-card-title>
        <v-form ref="form" lazy-validation @submit.prevent="submitService">
          <v-text-field
            v-model="currentService.title"
            type="text"
            label="Name"
            :rules="requiredRule"
          />
          <v-select
            v-model="selectedCategories"
            multiple
            chips
            label="Associated categories"
            :items="categoryTitles"
            :rules="arrayRequired"
          />
          <v-combobox
            v-model="currentService.synonyms"
            multiple
            chips
            label="Synonyms"
            :items="[]"
          >
            <template v-slot:selection="{ item, parent }">
              <v-chip>
                <span class="pr-2">{{ item }}</span>
                <v-icon small @click="parent.selectItem(item)">
                  close
                </v-icon>
              </v-chip>
            </template>
          </v-combobox>
          <v-spacer />
          <v-btn type="submit" :disable="validServiceForm">
            Submit
          </v-btn>
          <v-btn @click="serviceDialogOpen = false">
            Cancel
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="tagDialogOpen" max-width="800px">
      <v-card flat tile height="100%" class="pa-4">
        <v-card-title class="display-1 secondary--text">
          {{ tagAlreadyExists ? "Edit Tag" : "Add a New Tag" }}
        </v-card-title>
        <v-form ref="form" lazy-validation @submit.prevent="submitTag">
          <v-text-field
            v-model="currentTag.title"
            type="text"
            label="Name"
            :rules="requiredRule"
          />
          <v-text-field
            v-model="associatedService"
            disabled
            type="text"
            label="Service"
          />
          <v-select
            v-model="selectedCategories"
            disabled
            multiple
            chips
            label="Associated categories"
            :items="categoryTitles"
          />
          <v-combobox
            v-model="currentTag.synonyms"
            multiple
            chips
            label="Synonyms"
            :items="[]"
          >
            <template v-slot:selection="{ item, parent }">
              <v-chip>
                <span class="pr-2">{{ item }}</span>
                <v-icon small @click="parent.selectItem(item)">
                  close
                </v-icon>
              </v-chip>
            </template>
          </v-combobox>
          <v-spacer />
          <v-btn type="submit">
            Submit
          </v-btn>
          <v-btn @click="tagDialogOpen = false">
            Cancel
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "lodash";

export default {
  data() {
    return {
      serviceDialogOpen: false,
      serviceAlreadyExists: false,
      currentService: {},
      selectedCategories: [],
      tagDialogOpen: false,
      tagAlreadyExists: false,
      currentTag: {},
      associatedService: "",
      associatedServiceId: "",
      requiredRule: [v => !!v || "Required"],
      arrayRequired: [
        v => !!v || "Required",
        v => v.length > 0 || "Item is required"
      ]
    };
  },
  computed: {
    services() {
      return this.$store.state.services;
    },
    categories() {
      return this.$store.state.categories;
    },
    categoryTitles() {
      return this.categories.map(category => category.title);
    }
  },
  mounted() {
    this.$store.dispatch("fetchServices");
    this.$store.dispatch("fetchCategories");
  },
  methods: {
    newService() {
      this.serviceAlreadyExists = false;
      this.currentService = {
        synonyms: []
      };
      this.selectedCategories = [];
      this.serviceDialogOpen = true;
    },
    editService(service) {
      this.serviceAlreadyExists = true;
      this.currentService = _.cloneDeep(service);
      this.selectedCategories = this.currentService.categories.map(
        category => category.title
      );
      this.serviceDialogOpen = true;
    },
    deleteService(id) {
      if (confirm("Are you sure you want to delete this service?")) {
        this.$store.dispatch("deleteService", { id });
      }
    },
    submitService() {
      // the API requires category information to be in UUID format
      this.currentService.categories = this.selectedCategories.map(
        sc => this.categories.find(c => sc === c.title).id
      );

      if (this.serviceAlreadyExists) {
        this.$store.dispatch("updateService", this.currentService);
      } else {
        this.$store.dispatch("addService", this.currentService);
      }
      this.serviceDialogOpen = false;
    },
    newTag(service) {
      this.tagAlreadyExists = false;
      this.currentTag = {
        synonyms: []
      };
      this.selectedCategories = service.categories.map(c => c.title);
      this.associatedService = service.title;
      this.associatedServiceId = service.id;
      this.tagDialogOpen = true;
    },
    editTag(tag) {
      this.tagAlreadyExists = true;
      this.currentTag = _.cloneDeep(tag);
      const parentService = this.services.find(s =>
        s.tags.find(t => t.id === tag.id)
      );
      this.selectedCategories = parentService.categories.map(c => c.title);
      this.associatedService = parentService.title;
      this.associatedServiceId = parentService.id;
      this.tagDialogOpen = true;
    },
    deleteTag(id) {
      if (confirm("Are you sure you want to delete this tag?")) {
        this.$store.dispatch("deleteTag", { id });
      }
    },
    submitTag() {
      // the API requires service information to be in UUID format
      this.currentTag.service_id = this.associatedServiceId;

      if (this.tagAlreadyExists) {
        this.$store.dispatch("updateTag", this.currentTag);
      } else {
        this.$store.dispatch("addTag", this.currentTag);
      }
      this.tagDialogOpen = false;
    }
  }
};
</script>
